import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import * as Sentry from '@sentry/ember';
import dragula from 'dragula';
import 'dragula/dist/dragula.css';
import { v4 as uuidv4 } from 'uuid';
import { neighborsOf, positionBetween } from 'scaleflow/utils/position';
import { positionComparator } from 'scaleflow/utils/sort-by-position';
import {
  flipAnimate,
  snapshotPositions,
} from 'scaleflow/utils/flip-animate-siblings';

// An unset week, or a week whose position never got set, sorts last.
function weekPosition(item) {
  const week = item.get('week');
  const position = week?.isTruthy ? week.get('position') : undefined;
  return typeof position === 'number' ? position : Infinity;
}

export default class ListListComponent extends Component {
  @service currentUser;
  @service('editor-active') editorActiveService;
  @service store;
  @service router;
  @service intl;
  @service('list-drag') listDrag;

  groupsDrake = null;
  groupsFlipPositions = null;

  // Tracks records whose createRecord save is still outstanding. WarpDrive's
  // own currentState.stateName only reflects "inFlight" reliably in dev/test
  // builds (RecordState is eagerly constructed there); in production it can
  // still read as not-inFlight for a record created moments ago, letting
  // removeEmptyItems() destroy it while its create request is genuinely
  // still pending — see Sentry FRONT-END-MW. This set doesn't depend on that
  // internal timing at all.
  pendingCreates = new Set();

  // Item dragging is owned by the `list-drag` service so that several lists can
  // share one dragula instance and exchange items. Lists that weren't opted in
  // to a shared scope get a private one, which behaves as the component-local
  // drake used to.
  instanceDragScope = `list-${uuidv4()}`;

  get dragScope() {
    return this.args.dragScope ?? this.instanceDragScope;
  }

  get groupsAreSortable() {
    return Boolean(this.args.sortableGroups) && this.groupBy === 'workstream';
  }

  get itemsAreSortable() {
    return Boolean(this.args.sortableItems);
  }

  // {{#each-in}} (object-key iteration) doesn't reliably reposition an existing keyed
  // entry when its enumeration order changes — unlike a plain {{#each}} over an array,
  // which is what the pre-existing week reorder relies on. This gives the template an
  // array view of the same dictionary so group reordering gets that same, proven
  // keyed-reorder behavior. groupedItems itself is untouched, so emptyList/
  // currentUserHasNoItems (which read it as a dictionary) don't need to change.
  get groupEntries() {
    if (!this.groupBy) return [];
    return Object.entries(this.groupedItems).map(([key, group]) => ({
      key,
      group,
    }));
  }

  get groupBy() {
    return this.args.groupBy ?? 'assignee';
  }

  @cached
  get groupedItems() {
    let itemsWithoutPosition = [];
    let itemsWithPosition = [];
    this.args.items.forEach((item) => {
      if (item['position'] == undefined) itemsWithoutPosition.push(item);
      else itemsWithPosition.push(item);
    });
    itemsWithPosition.sort((a, b) => a['position'] - b['position']);
    let allItems = itemsWithPosition.concat(itemsWithoutPosition);
    if (this.args.sortBy === 'week') allItems = this.sortItemsByWeek(allItems);
    if (this.groupBy) allItems = this.groupItemsBy(allItems, this.groupBy);
    return allItems;
  }

  // Position stays the ordering the list is built on — the week sort is layered
  // on top of it rather than replacing it, so items sharing a week keep the
  // order the user dragged them into, and items with no week yet collect at the
  // end instead of splitting the week sequence.
  sortItemsByWeek(items) {
    return items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const weekA = weekPosition(a.item);
        const weekB = weekPosition(b.item);
        if (weekA !== weekB) return weekA - weekB;
        return a.index - b.index;
      })
      .map(({ item }) => item);
  }

  get emptyList() {
    return this.groupBy
      ? Object.keys(this.groupedItems).length === 0
      : this.groupedItems.length === 0;
  }

  groupItemsBy(items, groupBy) {
    if (groupBy === 'assignee') return this.groupItemsByAssignee(items);
    if (groupBy === 'workstream') return this.groupItemsByWorkstream(items);
    return items;
  }

  groupItemsByWorkstream(items) {
    const identifier_key = 'workstream.id';
    const group_label_key = 'workstream.title';
    const group_position_key = 'workstream.position';
    const groupedList = {};
    const NONE = {
      title: this.intl.t('dictionary.unfiled'),
      items: [],
      type: {
        id: null,
        type: 'worsktream',
      },
    };
    items.forEach((item) => {
      if (!item.get(identifier_key)) return NONE.items.push(item);
      const key = `workstream-${item.get(identifier_key)}`;
      const ref = groupedList[key];
      if (ref && ref.items) return ref.items.push(item);
      else {
        groupedList[key] = {
          title: item.get(group_label_key),
          items: [item],
          position: item.get(group_position_key),
          meta: {
            id: item.get(identifier_key),
            type: 'workstream',
          },
        };
      }
    });
    const sortedGroupList = {};
    const sorted = Object.keys(groupedList).sort((a, b) =>
      positionComparator(groupedList[a], groupedList[b]),
    );
    sorted.forEach((key) => {
      sortedGroupList[key] = groupedList[key];
    });
    if (NONE.items.length !== 0) {
      sortedGroupList['NONE'] = NONE;
    }

    return sortedGroupList;
  }

  groupItemsByAssignee(items) {
    const names = [];
    const data = items.reduce((acc, item) => {
      const assignees = item.id !== null ? item.get('assignees') : [];
      let name = '';
      let id = '';
      if (assignees.length === 0) {
        name = item.get('author.name');
        id = item.get('author.id');
        if (acc[id]) acc[id].items.push(item);
        else {
          acc[id] = {
            title: name,
            items: [item],
            meta: { id, type: 'person' },
          };
        }
        names.push({ name, id });
      } else {
        assignees.forEach((assignee) => {
          name = assignee.get('name');
          id = assignee.get('id');
          if (acc[id]) acc[id].items.push(item);
          else {
            acc[id] = {
              title: name,
              items: [item],
              meta: { id, type: 'person' },
            };
          }
          names.push({ name, id });
        });
      }
      // }

      return acc;
    }, {});

    const sortedNames = names.sort((a, b) =>
      (a?.name || '').localeCompare?.(b?.name || ''),
    );
    const list = sortedNames.reduce((acc, { id }) => {
      acc[id] = data[id];
      return acc;
    }, {});

    return list;
  }

  @action
  onFastExit() {}

  @action
  addNewItem(group = undefined, item = undefined) {
    let belongsTo = this.args.parent;
    const parentType = belongsTo.constructor.modelName;
    let position = this.determinePosition(item, group);

    if (isNaN(position)) {
      Sentry.captureException('undefined position detected');
      return;
    }
    const author = this.currentUser.currentUser;
    let obj = {
      [parentType]: belongsTo,
      author,
      position: position,
    };

    if (this.groupBy === 'assignee') {
      if (group?.meta?.id && group.meta.id !== this.currentUser.id) {
        obj.assignees = [this.store.peekRecord('person', group.meta.id)];
      } else obj.assignees = [author];
    }

    if (this.groupBy === 'workstream') {
      if (group?.meta?.id) {
        obj.workstream = this.store.peekRecord('workstream', group.meta.id);
      }
      obj.assignees = [author];
    }

    if (!this.groupBy) {
      obj.assignees = [author];
    }
    if (parentType === 'week' || parentType === 'workstream') {
      obj.initiative = this.router.currentRoute.attributes;
    }
    let newItem = this.store.createRecord(this.args.type, obj);
    this.pendingCreates.add(newItem);

    newItem
      .save()
      .then((item) => {
        this.activate(item);
        if (item.id)
          document
            .querySelector(`#item--${item.id}`)
            ?.scrollIntoView?.({ block: 'nearest' });
      })
      .finally(() => {
        this.pendingCreates.delete(newItem);
      });
  }

  removeEmptyItems(item_to_keep) {
    this.args.items.slice().forEach((element) => {
      if (element.id === item_to_keep.id) {
        return;
      }
      if (
        (element.text_delta == undefined ||
          element?.text_delta?.ops?.length == 0) &&
        element.author.get('id') === this.currentUser.id &&
        !this.pendingCreates.has(element) &&
        !element.currentState.stateName.includes('inFlight')
      ) {
        element.destroyRecord();
      }
    });
  }

  activate(item) {
    this.removeEmptyItems(item);
    this.editorActiveService.setActiveItem(item ? item.id : null);
  }

  getRandomNumberBetween(min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  determinePosition(item, group) {
    // debugger;
    const msNow = new Date().getTime();
    const increment = this.getRandomNumberBetween(100, 200);
    // if no base item given, put new item at the end of the list
    if (item?.position === undefined) {
      let positionArray = this.args.items
        .slice()
        .filter((item) => !isNaN(item['position']))
        .map((item) => item.position);
      if (positionArray.length == 0) {
        return increment + msNow;
      } else {
        let max = Math.max(...positionArray);
        return max + increment;
      }
    }
    let relevant_group = group;

    //if ungrouped
    if (!this.groupBy) relevant_group = undefined;

    if (group) relevant_group = group;

    //if new item from an author without a list yet
    if (this.groupBy && !group)
      relevant_group = {
        title: group.title,
        items: [],
        meta: {
          id: group?.meta?.id ?? null,
        },
      };

    // a base item is given, find the position in the list
    let items = relevant_group ? relevant_group.items : this.groupedItems;

    let base_index = items.indexOf(item);

    if (items.length == 1 || item.position == -Infinity) {
      return item.position + increment;
    }

    //not found - to end of list
    if (base_index == -1) {
      return Math.max(...this.args.items.mapBy('position')) + increment;
    }

    //end of list - add 20
    if (base_index == items.length - 1) {
      return items[base_index].position + increment;
    }

    //middle of list
    if (base_index < items.length - 1) {
      return parseInt(
        (items[base_index].position + items[base_index + 1].position) / 2,
      );
    }
  }

  get currentUserHasNoItems() {
    return (
      this.groupBy === 'assignee' &&
      (this.groupedItems[this.currentUser.id] == undefined ||
        this.groupedItems[this.currentUser.id]?.items?.length == 0)
    );
  }

  @action
  editParentTitle() {
    this.args?.editParentTitle?.();
  }

  @action
  registerGroupContainer(element) {
    this.groupsDrake = dragula({
      containers: [element],
      revertOnSpill: true,
      direction: 'vertical',
      moves: (_, __, handle) =>
        handle?.classList?.contains('group-sort-icon') ?? false,
    });
    this.groupsDrake.on('drop', this.onGroupDrop);

    // dragula repositions siblings live as you drag (its 'shadow' event fires
    // right after each reposition, per its own source), with no animation of
    // its own. FLIP-animate those shifts: snapshot positions at drag start,
    // then on every subsequent reposition, animate the delta from the last
    // snapshot and save the new one as the baseline for next time.
    this.groupsDrake.on('drag', (dragged) => {
      this.groupsFlipPositions = snapshotPositions(element, dragged);
    });
    this.groupsDrake.on('shadow', (dragged, container) => {
      this.groupsFlipPositions = flipAnimate(
        container,
        this.groupsFlipPositions ?? new Map(),
        dragged,
      );
    });
    this.groupsDrake.on('dragend', () => {
      this.groupsFlipPositions = null;
    });
  }

  @action
  unregisterGroupContainer() {
    this.groupsDrake?.destroy();
    this.groupsDrake = null;
    this.groupsFlipPositions = null;
  }

  @action
  onGroupDrop(el, target) {
    const movedId = el.dataset.groupId;
    if (!movedId) return;
    const moved = this.store.peekRecord('workstream', movedId);
    if (!moved) return;

    const { beforeId, afterId } = neighborsOf(el, target, 'groupId');
    const before = beforeId
      ? this.store.peekRecord('workstream', beforeId)
      : null;
    const after = afterId ? this.store.peekRecord('workstream', afterId) : null;

    moved.position = positionBetween(before?.position, after?.position);
    moved.save();
  }

  @action
  registerItemContainer(element) {
    this.listDrag.registerContainer(this.dragScope, element, this);
  }

  @action
  unregisterItemContainer(element) {
    this.listDrag.unregisterContainer(this.dragScope, element);
  }
}
